import { PayerRow, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { queriesPayerRows } from "@query";

/**
 * Indexes the payee's client (payer) search rows by engagement id, so a work-log
 * table can name the client each row belongs to.
 *
 * v3 `WorkLog` carries no `payerId` — the only counterparty handle on it is the
 * readonly `payerEngagementId` — and `searchPayerRows` exposes that filter with
 * `eq` only, so a page of work logs cannot be joined in one request. The
 * caller's full client set is fetched once instead and every engagement indexed
 * to its owning row, which is the same join `useQueryWorkLogsWithPayees` makes
 * for the payer-side contractor column. A row can hold several engagements, so
 * the map is keyed on each `engagements[].id`, not on the row id.
 */
export const usePayersMap = (workLogs: WorkLog[] | undefined) => {
  const hasWorkLogs = (workLogs?.length ?? 0) > 0;

  const queryPayerRows = queriesPayerRows.searchPayerRows.useQueryAll(
    undefined,
    { enabled: hasWorkLogs }
  );

  const payerRows = queryPayerRows.flatData;

  return useMemo(() => {
    const map = new Map<string, PayerRow>();
    payerRows.forEach(payerRow => {
      payerRow.engagements?.forEach(engagement => {
        if (engagement.id) {
          map.set(engagement.id, payerRow);
        }
      });
    });
    return map;
  }, [payerRows]);
};
